import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Users, Eye, MousePointerClick, Timer } from "lucide-react";
import { getOverview } from "@/lib/analyticsApi";
import { formatError } from "@/lib/api";
import {
  NEON, PINK, CYAN, Card, Kpi, Loading, Empty, Table, Th, Td, MiniBar, fmtMs, fmtNum,
  TOOLTIP_STYLE, TOOLTIP_ITEM, TOOLTIP_LABEL,
} from "./shared";

const AXIS = { stroke: "rgba(255,255,255,0.35)", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" };

const RankTable = ({ title, rows, labelKey, valueKey, color = NEON, emptyLabel }) => {
  const max = rows && rows.length ? Math.max(...rows.map((r) => r[valueKey] || 0)) : 0;
  return (
    <Card title={title}>
      {!rows || rows.length === 0 ? (
        <Empty label={emptyLabel || "No data yet."} />
      ) : (
        <Table>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <Td className="max-w-[220px] truncate">{r[labelKey] || "—"}</Td>
                <Td className="w-1/2">
                  <MiniBar value={r[valueKey] || 0} max={max} color={color} />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
};

const AnalyticsOverview = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getOverview(days)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => toast.error(formatError(e?.response?.data?.detail, "Failed to load overview.")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) return <Loading />;
  if (!data) return <Empty label="No analytics data yet." />;

  const byDay = (data.by_day || []).map((d) => ({
    date: d.date,
    short: (d.date || "").slice(5), // MM-DD
    visitors: d.visitors || 0,
    pageviews: d.pageviews || 0,
  }));
  const topPages = data.top_pages || [];
  const maxViews = topPages.length ? Math.max(...topPages.map((p) => p.views || 0)) : 0;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Unique Visitors" value={fmtNum(data.unique_visitors)} accent={NEON}
          sub={<span className="inline-flex items-center gap-1"><Users size={11} /> last {days}d</span>} />
        <Kpi label="Pageviews" value={fmtNum(data.pageviews)} accent={CYAN}
          sub={<span className="inline-flex items-center gap-1"><Eye size={11} /> total loads</span>} />
        <Kpi label="Sessions" value={fmtNum(data.sessions)} accent={PINK}
          sub={<span className="inline-flex items-center gap-1"><MousePointerClick size={11} /> visits</span>} />
        <Kpi label="Avg Engaged" value={fmtMs(data.avg_engage_ms)} accent="#ffae00"
          sub={<span className="inline-flex items-center gap-1"><Timer size={11} /> mm:ss / view</span>} />
      </div>

      {/* Traffic over time */}
      <Card title="Traffic Over Time">
        {byDay.length === 0 ? (
          <Empty label="No traffic recorded in this range." />
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={byDay} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CYAN} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={CYAN} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NEON} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={NEON} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="short" tick={AXIS} tickLine={false} axisLine={false} minTickGap={16} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={42} />
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM} labelStyle={TOOLTIP_LABEL}
                  labelFormatter={(l, p) => (p && p[0] ? p[0].payload.date : l)} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                <Area type="monotone" dataKey="pageviews" name="Pageviews" stroke={CYAN} strokeWidth={2} fill="url(#gPv)" />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke={NEON} strokeWidth={2} fill="url(#gVis)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Top pages engagement table */}
      <Card title="Top Pages">
        {topPages.length === 0 ? (
          <Empty label="No page activity yet." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Path</Th>
                <Th>Views</Th>
                <Th>Avg Time</Th>
                <Th>Avg Scroll</Th>
              </tr>
            </thead>
            <tbody>
              {topPages.map((p, i) => (
                <tr key={i}>
                  <Td className="font-mono text-white/90 max-w-[280px] truncate">{p.path}</Td>
                  <Td className="w-[34%]"><MiniBar value={p.views || 0} max={maxViews} color={NEON} /></Td>
                  <Td className="tabular-nums">{fmtMs(p.avg_ms)}</Td>
                  <Td className="tabular-nums">{Math.round(p.avg_scroll || 0)}%</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Breakdown tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankTable title="Devices" rows={data.devices} labelKey="device_type" valueKey="count" color={NEON} emptyLabel="No device data." />
        <RankTable title="Browsers" rows={data.browsers} labelKey="browser" valueKey="count" color={CYAN} emptyLabel="No browser data." />
        <RankTable title="Countries" rows={data.countries} labelKey="country" valueKey="count" color={PINK} emptyLabel="No location data yet." />
        <RankTable title="Top Referrers" rows={data.top_referrers} labelKey="referrer" valueKey="count" color="#ffae00" emptyLabel="No referrers." />
      </div>
    </div>
  );
};

export default AnalyticsOverview;
